import { z } from "zod";
import { optionalText, text, uuid } from "./common";

/*
 * Prescriptions and the clinic formulary (Phase 3).
 *
 * Rules (see docs/architecture/10-security.md, AI safety):
 *  - A prescription starts as a draft. Only the prescribing doctor, signed in, can issue it.
 *    AI/automation may at most create drafts.
 *  - Issued prescriptions are never edited. Corrections: void (with a reason) + a new prescription
 *    ("copy to new draft"). Voids are final. The database enforces both.
 *
 * The formulary starts EMPTY for every clinic. DentOS never seeds medicines, strengths or default
 * dosages: that is medical content the clinic's own doctors must choose and take responsibility for.
 */

export const MEDICINE_FORMS = [
  "tablet", "capsule", "syrup", "suspension", "mouthwash", "gel", "ointment", "cream",
  "injection", "drops", "spray", "lozenge", "powder", "other",
  // Ayurveda
  "kashayam", "arishtam", "asavam", "churnam", "gulika", "ghritam", "tailam", "lehyam", "bhasma",
] as const;
export type MedicineForm = (typeof MEDICINE_FORMS)[number];
export const AYURVEDA_FORMS = ["kashayam", "arishtam", "asavam", "churnam", "gulika", "ghritam", "tailam", "lehyam", "bhasma"] as const satisfies readonly MedicineForm[];

export const MEDICINE_ROUTES = [
  "oral", "topical", "sublingual", "buccal", "intramuscular", "intravenous", "inhalation", "local_infiltration", "nasal", "rectal", "other",
] as const;
export type MedicineRoute = (typeof MEDICINE_ROUTES)[number];

export const FOOD_TIMINGS = ["before", "after", "with", "empty", "any"] as const;
export type FoodTiming = (typeof FOOD_TIMINGS)[number];

/** Times of day a medicine is taken (Kerala prescriptions are written this way: "6 am and 6 pm"). */
export const TIMING_SLOTS = ["early_morning", "morning", "noon", "evening", "night", "bedtime"] as const;
export type TimingSlot = (typeof TIMING_SLOTS)[number];

/** "2 times a day" from the chosen times, used when no frequency is typed. */
export const frequencyFromSlots = (slots: readonly string[]) => (slots.length === 0 ? "" : slots.length === 1 ? "Once a day" : `${slots.length} times a day`);

export const PRESCRIPTION_STATUSES = ["draft", "issued", "void"] as const;
export type PrescriptionStatus = (typeof PRESCRIPTION_STATUSES)[number];

// ---------------------------------------------------------------------------
// Formulary
// ---------------------------------------------------------------------------

export const formularyMedicineInput = z.object({
  name: text(200),
  genericName: optionalText(200),
  form: z.enum(MEDICINE_FORMS),
  strength: optionalText(60),
  defaultDosage: optionalText(100),
  defaultFrequency: optionalText(100),
  defaultDuration: optionalText(100),
  defaultRoute: z.enum(MEDICINE_ROUTES).nullish().transform((v) => v ?? null),
  defaultInstructions: optionalText(500),
  manufacturer: optionalText(120),
  packSize: optionalText(60),
  defaultDilution: optionalText(120),
  defaultAnupana: optionalText(120),
  isActive: z.boolean().default(true),
});
export type FormularyMedicineInput = z.infer<typeof formularyMedicineInput>;

export const formularyQuery = z.object({
  q: z.string().trim().max(100).optional(),
  /** Include deactivated medicines (formulary management screen). */
  includeInactive: z
    .enum(["true", "false"])
    .optional()
    .transform((v) => v === "true"),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export interface FormularyMedicine {
  id: string;
  name: string;
  genericName: string | null;
  form: MedicineForm;
  strength: string | null;
  defaultDosage: string | null;
  defaultFrequency: string | null;
  defaultDuration: string | null;
  defaultRoute: MedicineRoute | null;
  defaultInstructions: string | null;
  manufacturer: string | null;
  packSize: string | null;
  defaultDilution: string | null;
  defaultAnupana: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Prescriptions
// ---------------------------------------------------------------------------

export const prescriptionItemInput = z.object({
  /** Formulary link; null for a free-text medicine not in the formulary. */
  medicineId: uuid.nullish().transform((v) => v ?? null),
  /** Snapshot printed on the prescription (kept even if the formulary entry changes later). */
  medicineName: text(200),
  form: optionalText(60),
  strength: optionalText(60),
  dosage: text(100),
  /** May be left empty when times of day are chosen; it is then written as "2 times a day". */
  frequency: z.string().trim().max(100).default(""),
  duration: optionalText(100),
  durationDays: z.number().int().min(1).max(365).nullish().transform((v) => v ?? null),
  route: z.enum(MEDICINE_ROUTES).nullish().transform((v) => v ?? null),
  foodTiming: z.enum(FOOD_TIMINGS).default("any"),
  instructions: optionalText(500),
  manufacturer: optionalText(120),
  /** e.g. "with 45 ml warm water" */
  dilution: optionalText(120),
  /** What it is taken with: warm water, honey, ghee, buttermilk… */
  anupana: optionalText(120),
  timingSlots: z.array(z.enum(TIMING_SLOTS)).max(6).default([]).transform((a) => [...new Set(a)]),
}).refine((i) => i.frequency !== "" || i.timingSlots.length > 0, { path: ["frequency"], message: "Give how often, or choose the times of day" });
export type PrescriptionItemInput = z.infer<typeof prescriptionItemInput>;

export const createPrescriptionInput = z.object({
  visitId: uuid.nullish().transform((v) => v ?? null),
  notes: optionalText(2000),
  items: z.array(prescriptionItemInput).max(30).default([]),
});
export type CreatePrescriptionInput = z.infer<typeof createPrescriptionInput>;

/** Replaces the notes and ALL lines of a draft. */
export const updatePrescriptionInput = z.object({
  version: z.number().int().min(1),
  visitId: uuid.nullish().transform((v) => v ?? null),
  notes: optionalText(2000),
  items: z.array(prescriptionItemInput).max(30),
});

export const issuePrescriptionInput = z.object({
  version: z.number().int().min(1),
  /** Must be true to issue when safety warnings exist; the acknowledged warnings are audited. */
  acknowledgeWarnings: z.boolean().optional(),
});

export const voidPrescriptionInput = z.object({
  /** Optional optimistic-concurrency check (recommended for the UI). */
  version: z.number().int().min(1).optional(),
  reason: text(500),
});

export interface PrescriptionItem {
  id: string;
  sequence: number;
  medicineId: string | null;
  medicineName: string;
  genericName: string | null;
  form: string | null;
  strength: string | null;
  dosage: string;
  frequency: string;
  duration: string | null;
  durationDays: number | null;
  route: MedicineRoute | null;
  foodTiming: FoodTiming;
  instructions: string | null;
  manufacturer: string | null;
  dilution: string | null;
  anupana: string | null;
  timingSlots: TimingSlot[];
}

export interface Prescription {
  id: string;
  patientId: string;
  doctorId: string;
  doctorName: string;
  visitId: string | null;
  prescriptionNo: string | null;
  status: PrescriptionStatus;
  notes: string | null;
  issuedAt: string | null;
  issuedBy: string | null;
  voidedAt: string | null;
  voidedBy: string | null;
  voidReason: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  items: PrescriptionItem[];
}

export type PrescriptionListItem = Omit<Prescription, "items"> & { itemCount: number };

/** A safety warning computed when issuing. The doctor must acknowledge them explicitly. */
export interface PrescriptionWarning {
  kind: "allergy" | "duplicate_medicine";
  message: string;
  /** Line sequence numbers concerned. */
  itemSequences: number[];
  allergyId?: string;
  allergen?: string;
  severity?: string;
}

/** JSON for a printable/shareable prescription. The web app renders it (no PDF library). */
export interface PrescriptionPrint {
  /** "DRAFT – not valid" for drafts, "VOID" for voided prescriptions, null when issued. */
  watermark: string | null;
  status: PrescriptionStatus;
  clinic: {
    name: string;
    legalName: string | null;
    addressLines: string[];
    phone: string | null;
    email: string | null;
    taxRegistrationLabel: string | null;
    taxRegistrationNumber: string | null;
  };
  doctor: {
    name: string;
    title: string | null;
    qualifications: string | null;
    registrationNumber: string | null;
    registrationCouncil: string | null;
  };
  patient: {
    name: string;
    code: string;
    ageYears: number | null;
    gender: string | null;
  };
  prescriptionNo: string | null;
  date: string;
  items: PrescriptionItem[];
  notes: string | null;
  voidReason: string | null;
}

/** Paste / import the clinic's own medicine list (e.g. copied from Excel). Duplicates are skipped. */
export const medicineImportInput = z.object({
  rows: z
    .array(
      z.object({
        name: text(200),
        form: z.enum(MEDICINE_FORMS),
        manufacturer: optionalText(120),
        packSize: optionalText(60),
        genericName: optionalText(200),
      }),
    )
    .min(1)
    .max(500),
});
export interface MedicineImportResult {
  added: number;
  skipped: number;
}
